import {
  type Announcements,
  closestCorners,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  TouchSensor,
  type UniqueIdentifier,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { BOARD_STATUSES, type BoardTasksInput, type TaskStatus, type TaskSummary } from '@fos/shared/contracts/work';
import { TASK_REQUEST_COLUMN_LABEL, TASK_STATUS_LABELS } from '@fos/shared/labels';
import { CircleCheck, SquareCheckBig } from 'lucide-react';
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { cn } from '../../../lib/cn';
import { plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { CountPill } from '../../../ui/CountPill';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { PoolChip, useServicePool } from '../../maintenance/components/ServicePool';
import { TOUCH_BUTTON } from '../../mobile';
import { QUICK_DONE_TOAST_MS, taskErrorToast, useToday } from '../hooks/useTasks';
import {
  afterIdOf,
  applyToBoard,
  type BoardColumns,
  type BoardStatus,
  canEnterColumn,
  columnId,
  columnsFromBoard,
  DONE_ZONE_ID,
  findColumn,
  insertIntoColumns,
  moveInColumns,
  removeFromColumns,
  statusFromColumnId,
} from '../lib/board';
import { dueInfo } from '../lib/due';
import {
  AiStatus,
  AssigneeAvatar,
  DelegatedLabel,
  DoneToggle,
  DueText,
  FromClientBadge,
  isDelegatedByMe,
  PriorityFlag,
  TaskChips,
  useMeId,
} from './parts';

/**
 * Tablica Kanban (Figma 04 Zadania / Tablica, 3019:2337): cztery kolumny otwartych statusów, karty na bg/surface,
 * na dole „N gotowych w tym tygodniu” — w trakcie przeciągania to strefa „Gotowe”.
 * Przeciąganie myszą, dotykiem i z klawiatury (spacja = podnieś/upuść, strzałki = miejsce i kolumna, Esc = anuluj,
 * Enter = otwórz kartę). Zmiana trafia od razu do pamięci podręcznej (optymistycznie), a po zapisie odświeżają się
 * wszystkie widoki zadań (lista, karta, Dziś).
 *
 * Lifting 17.10.2026 (DECISIONS „Zadania łatwiejsze w użyciu”): na karcie okrągły znacznik „Gotowe” (desktop — przy
 * najechaniu albo fokusie, telefon — zawsze), chipy klienta i projektu, termin w kolorze znaczenia, flaga wysokiego
 * i pilnego priorytetu; liczniki kolumn jako CountPill. `layout="snap"` (telefon): kolumny przewijane w poziomie
 * z przyciąganiem, przeciąganie karty po przytrzymaniu (dotyk), żeby przesunięcie palcem przewijało tablicę.
 */

type BoardInput = NonNullable<BoardTasksInput>;

/** Nagłówek kolumny: `request` = „Zgłoszenia od klientów” (17.10.2026), reszta — etykieta statusu. */
function columnLabel(status: TaskStatus): string {
  return status === 'request' ? TASK_REQUEST_COLUMN_LABEL : TASK_STATUS_LABELS[status];
}

const INSTRUCTIONS =
  'Aby przenieść zadanie, naciśnij spację. Strzałkami zmieniasz miejsce i kolumnę, spacją upuszczasz, Escape anuluje. Enter otwiera zadanie.';

export function TaskBoard({
  input,
  emptyAction,
  layout = 'grid',
}: {
  input: BoardInput;
  emptyAction?: ReactNode;
  /** grid — cztery kolumny obok siebie (desktop), snap — kolumny przewijane w poziomie z przyciąganiem (telefon) */
  layout?: 'grid' | 'snap';
}) {
  const navigate = useNavigate();
  const today = useToday();
  const utils = trpc.useUtils();
  const board = trpc.tasks.board.useQuery(input);

  const [columns, setColumns] = useState<BoardColumns>(() => columnsFromBoard(board.data));
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overDone, setOverDone] = useState(false);
  const snapshot = useRef<BoardColumns | null>(null);
  const startColumn = useRef<BoardStatus | null>(null);
  const lastDragEnd = useRef(0);
  const dragging = useRef(false);

  useEffect(() => {
    if (!activeId) setColumns(columnsFromBoard(board.data));
  }, [board.data, activeId]);

  const pointer = useSensor(PointerSensor, { activationConstraint: { distance: 6 } });
  // telefon: przytrzymanie karty (250 ms) zaczyna przeciąganie; ruch palcem wcześniej = przewijanie tablicy
  const touch = useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } });
  const keyboard = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
    keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space', 'Enter'] },
  });
  const sensors = useSensors(...(layout === 'snap' ? [touch, keyboard] : [pointer, keyboard]));

  const byId = useMemo(() => {
    const map = new Map<string, TaskSummary>();
    for (const s of BOARD_STATUSES) for (const t of columns[s]) map.set(t.id, t);
    return map;
  }, [columns]);
  const activeTask = activeId ? (byId.get(activeId) ?? null) : null;

  const titleOf = (id: UniqueIdentifier) => byId.get(String(id))?.title ?? 'zadanie';
  const placeOf = (id: UniqueIdentifier | undefined) => {
    if (id === undefined) return '';
    if (id === DONE_ZONE_ID) return 'Gotowe';
    const status = findColumn(columns, String(id));
    return status ? columnLabel(status) : '';
  };
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Podniesiono zadanie ${titleOf(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over
        ? `Zadanie ${titleOf(active.id)} nad kolumną ${placeOf(over.id)}.`
        : `Zadanie ${titleOf(active.id)} poza tablicą.`,
    onDragEnd: ({ active, over }) =>
      over
        ? `Upuszczono zadanie ${titleOf(active.id)} w kolumnie ${placeOf(over.id)}.`
        : `Upuszczono zadanie ${titleOf(active.id)}.`,
    onDragCancel: ({ active }) => `Anulowano przenoszenie zadania ${titleOf(active.id)}.`,
  };

  const reset = () => {
    dragging.current = false;
    setActiveId(null);
    setOverDone(false);
    lastDragEnd.current = Date.now();
  };

  const onDragStart = ({ active }: DragStartEvent) => {
    dragging.current = true;
    setActiveId(String(active.id));
    snapshot.current = columns;
    startColumn.current = findColumn(columns, String(active.id));
  };

  const onDragOver = ({ active, over }: DragOverEvent) => {
    if (!over) return;
    if (over.id === DONE_ZONE_ID) {
      setOverDone(true);
      return;
    }
    setOverDone(false);
    const id = String(active.id);
    setColumns((current) => {
      const from = findColumn(current, id);
      const to = findColumn(current, String(over.id));
      if (!from || !to || from === to) return current;
      const dragged = current[from].find((t) => t.id === id);
      if (!dragged || !canEnterColumn(dragged, startColumn.current, to)) return current;
      const target = current[to];
      let index = target.length;
      if (!statusFromColumnId(String(over.id))) {
        const overIndex = target.findIndex((t) => t.id === over.id);
        const translated = active.rect.current.translated;
        const below = translated ? translated.top > over.rect.top + over.rect.height / 2 : false;
        index = overIndex >= 0 ? overIndex + (below ? 1 : 0) : target.length;
      }
      return moveInColumns(current, id, to, index);
    });
  };

  /**
   * Zapis przeniesienia (`tasks.move`, optymistycznie). Przy „Gotowe” (strefa albo znacznik na karcie) toast
   * „Cofnij” od razu; cofnięcie wraca do poprzedniej kolumny i miejsca. Wywołania przez `utils.client`, więc
   * działają także po zmianie widoku.
   */
  const persist = (task: TaskSummary, next: BoardColumns, status: BoardStatus | 'done', previous: BoardColumns) => {
    const doneDelta = status === 'done' ? 1 : 0;
    utils.tasks.board.setData(input, (old) => (old ? applyToBoard(old, next, doneDelta) : old));
    const afterId = status === 'done' ? null : afterIdOf(next, status, task.id);
    const saving = utils.client.tasks.move.mutate({ id: task.id, status, afterId });
    saving.then(
      () => void utils.tasks.invalidate(),
      (err: unknown) => {
        setColumns(previous);
        utils.tasks.board.setData(input, (old) => (old ? applyToBoard(old, previous, -doneDelta) : old));
        taskErrorToast(err);
        void utils.tasks.invalidate();
      },
    );
    if (status !== 'done') return saving;
    const from = findColumn(previous, task.id);
    const index = from ? previous[from].findIndex((t) => t.id === task.id) : -1;
    toast.success('Oznaczono jako gotowe', {
      description: task.title,
      duration: QUICK_DONE_TOAST_MS,
      action: {
        label: 'Cofnij',
        onClick: () => {
          if (!from) return;
          utils.tasks.board.setData(input, (old) =>
            old ? applyToBoard(old, insertIntoColumns(columnsFromBoard(old), task, from, index), -1) : old,
          );
          saving
            .then(
              (saved) =>
                utils.client.tasks.move
                  .mutate({ id: saved.id, status: from, afterId: afterIdOf(previous, from, task.id) })
                  .catch(taskErrorToast),
              () => undefined,
            )
            .finally(() => void utils.tasks.invalidate());
        },
      },
    });
    return saving;
  };

  /**
   * „Przyjmij” na karcie zgłoszenia od klienta (17.10.2026): zgłoszenie idzie na górę „Do zrobienia” (od razu,
   * z toastem „Cofnij” — powrót do zgłoszeń na to samo miejsce). Klient zobaczy w portalu „Zaplanowane”.
   */
  const acceptRequest = (task: TaskSummary) => {
    const previous = columns;
    const index = previous.request.findIndex((t) => t.id === task.id);
    const next = moveInColumns(previous, task.id, 'todo', 0);
    setColumns(next);
    const saving = persist(task, next, 'todo', previous);
    toast.success('Zgłoszenie przyjęte', {
      description: `${task.title} — w „Do zrobienia”`,
      duration: QUICK_DONE_TOAST_MS,
      action: {
        label: 'Cofnij',
        onClick: () => {
          utils.tasks.board.setData(input, (old) =>
            old ? applyToBoard(old, moveInColumns(columnsFromBoard(old), task.id, 'request', index)) : old,
          );
          saving
            .then(
              () =>
                utils.client.tasks.move
                  .mutate({ id: task.id, status: 'request', afterId: afterIdOf(previous, 'request', task.id) })
                  .catch(taskErrorToast),
              () => undefined,
            )
            .finally(() => void utils.tasks.invalidate());
        },
      },
    });
  };

  /** Znacznik „Gotowe” na karcie: karta znika od razu (jak upuszczenie na strefę „Gotowe”). */
  const completeTask = (task: TaskSummary) => {
    const next = removeFromColumns(columns, task.id);
    setColumns(next);
    persist(task, next, 'done', columns);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const id = String(active.id);
    const previous = snapshot.current ?? columns;
    const task = [...BOARD_STATUSES].flatMap((s) => previous[s]).find((t) => t.id === id);
    reset();
    if (!over || !task) {
      setColumns(previous);
      return;
    }
    if (over.id === DONE_ZONE_ID) {
      const next = removeFromColumns(previous, id);
      setColumns(next);
      persist(task, next, 'done', previous);
      return;
    }
    const col = findColumn(columns, id);
    if (!col) {
      setColumns(previous);
      return;
    }
    let next = columns;
    const overCol = findColumn(columns, String(over.id));
    if (overCol === col && over.id !== id && !statusFromColumnId(String(over.id))) {
      const oldIndex = columns[col].findIndex((t) => t.id === id);
      const newIndex = columns[col].findIndex((t) => t.id === over.id);
      if (oldIndex >= 0 && newIndex >= 0) next = { ...columns, [col]: arrayMove(columns[col], oldIndex, newIndex) };
    }
    setColumns(next);
    const from = startColumn.current;
    const startIndex = from ? previous[from].findIndex((t) => t.id === id) : -1;
    const endIndex = next[col].findIndex((t) => t.id === id);
    if (from === col && startIndex === endIndex) return;
    persist(task, next, col, previous);
  };

  const onDragCancel = () => {
    if (snapshot.current) setColumns(snapshot.current);
    reset();
  };

  const open = (task: TaskSummary) => {
    // Nie otwieraj karty przy podnoszeniu / upuszczaniu (spacja, puszczenie myszy po przeciągnięciu).
    if (dragging.current || Date.now() - lastDragEnd.current < 250) return;
    navigate(`/zadania/${task.id}`);
  };

  if (board.isPending) return <BoardSkeleton layout={layout} />;
  if (board.isError) {
    return (
      <Section>
        <ErrorState onRetry={() => void board.refetch()} retrying={board.isFetching} />
      </Section>
    );
  }

  const empty = BOARD_STATUSES.every((s) => columns[s].length === 0) && !activeId;
  // kolumna zgłoszeń od klientów (17.10.2026): tylko gdy workspace ma portal albo zgłoszenia (requestsEnabled)
  const visible = BOARD_STATUSES.filter((s) => s !== 'request' || board.data.requestsEnabled || columns[s].length > 0);
  const doneCount = board.data.doneThisWeek;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      accessibility={{ announcements, screenReaderInstructions: { draggable: INSTRUCTIONS } }}
    >
      {empty ? (
        <Section>
          <EmptyState
            icon={SquareCheckBig}
            title="Brak otwartych zadań"
            description="Nic tu nie czeka. Dodaj zadanie albo zmień filtry."
            action={emptyAction}
          />
        </Section>
      ) : layout === 'snap' ? (
        <div
          data-board-layout="snap"
          className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto overscroll-x-contain px-4 pb-2 [scrollbar-width:none]"
        >
          {visible.map((status) => (
            <div key={status} className="flex w-[84%] max-w-[340px] shrink-0 snap-start">
              <BoardColumnFor
                status={status}
                board={board.data}
                columns={columns}
                dragging={activeId !== null}
                today={today}
                onOpen={open}
                onComplete={completeTask}
                onAccept={acceptRequest}
                alwaysShowDone
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="-mx-4 overflow-x-auto px-4 pb-2 md:-mx-12 md:px-12">
          <div
            className="grid gap-5 lg:gap-7"
            style={{
              gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))`,
              minWidth: visible.length * 220,
            }}
          >
            {visible.map((status) => (
              <BoardColumnFor
                key={status}
                status={status}
                board={board.data}
                columns={columns}
                dragging={activeId !== null}
                today={today}
                onOpen={open}
                onComplete={completeTask}
                onAccept={acceptRequest}
              />
            ))}
          </div>
        </div>
      )}
      <DoneZone dragging={activeId !== null} over={overDone} count={doneCount} />
      <DragOverlay dropAnimation={null}>
        {activeTask ? (
          <div className="cursor-grabbing rounded-[10px] border border-line bg-panel px-4 py-3.5 shadow-popover">
            <CardBody task={activeTask} today={today} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function BoardColumnFor({
  status,
  board,
  columns,
  ...rest
}: {
  status: BoardStatus;
  board: { columns: { status: string; total: number; truncated: boolean }[] };
  columns: BoardColumns;
  dragging: boolean;
  today: string;
  onOpen: (task: TaskSummary) => void;
  onComplete: (task: TaskSummary) => void;
  onAccept: (task: TaskSummary) => void;
  alwaysShowDone?: boolean;
}) {
  const col = board.columns.find((c) => c.status === status);
  return (
    <BoardColumn
      status={status}
      items={columns[status]}
      total={col?.truncated ? col.total : columns[status].length}
      truncated={col?.truncated ?? false}
      {...rest}
    />
  );
}

function BoardColumn({
  status,
  items,
  total,
  truncated,
  dragging,
  today,
  onOpen,
  onComplete,
  onAccept,
  alwaysShowDone = false,
}: {
  status: BoardStatus;
  items: TaskSummary[];
  total: number;
  truncated: boolean;
  dragging: boolean;
  today: string;
  onOpen: (task: TaskSummary) => void;
  onComplete: (task: TaskSummary) => void;
  onAccept: (task: TaskSummary) => void;
  alwaysShowDone?: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: columnId(status) });
  const headingId = `kolumna-${status}`;
  return (
    <section aria-labelledby={headingId} className="flex w-full min-w-0 flex-col">
      <h2 id={headingId} className="flex h-[25px] items-center gap-2 text-body-s font-medium text-fg">
        {columnLabel(status)}
        <CountPill count={total} tone={status === 'request' && total > 0 ? 'accent' : 'neutral'} />
      </h2>
      <SortableContext items={items.map((t) => t.id)} strategy={verticalListSortingStrategy}>
        <ul
          ref={setNodeRef}
          aria-labelledby={headingId}
          className={cn(
            'mt-2.5 flex min-h-[74px] flex-1 flex-col gap-2.5 rounded-[10px] transition-colors',
            dragging && items.length === 0 && 'border border-dashed border-line',
            dragging && isOver && items.length === 0 && 'border-accent bg-accent-subtle',
          )}
        >
          {items.map((task) => (
            <SortableCard
              key={task.id}
              task={task}
              today={today}
              onOpen={onOpen}
              onComplete={onComplete}
              onAccept={onAccept}
              alwaysShowDone={alwaysShowDone}
            />
          ))}
          {status === 'request' && items.length === 0 && !dragging && (
            <li className="list-none rounded-[10px] border border-dashed border-line px-4 py-3.5 text-caption text-fg-muted">
              Nowe zgłoszenia z portalu klienta trafią tutaj. Przyjmij je albo przenieś do innej kolumny.
            </li>
          )}
        </ul>
      </SortableContext>
      {truncated && (
        <p className="mt-2 text-caption text-fg-muted">
          Widać {items.length} z {total}. Zawęź filtry albo przejdź do listy.
        </p>
      )}
    </section>
  );
}

function SortableCard({
  task,
  today,
  onOpen,
  onComplete,
  onAccept,
  alwaysShowDone,
}: {
  task: TaskSummary;
  today: string;
  onOpen: (task: TaskSummary) => void;
  onComplete: (task: TaskSummary) => void;
  onAccept: (task: TaskSummary) => void;
  alwaysShowDone: boolean;
}) {
  // zgłoszenie od klienta (17.10.2026): zamiast „Gotowe” — „Przyjmij” (→ Do zrobienia)
  const request = task.status === 'request';
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: !task.canEdit,
    attributes: { roleDescription: 'zadanie na tablicy' },
  });
  const style: CSSProperties = {
    transform: transform ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)` : undefined,
    transition,
  };
  return (
    <li ref={setNodeRef} className="group/card relative list-none" style={style}>
      {/* Przycisk: Enter / klik otwiera kartę, spacja podnosi (KeyboardSensor), przeciąganie myszą od 6 px. */}
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        onClick={() => onOpen(task)}
        className={cn(
          // kolumny na szarym tle aplikacji, karty białe (DECISIONS 2026-10-07 „Nowy układ wizualny”)
          'block w-full cursor-pointer rounded-[10px] border border-line bg-panel px-4 py-3.5 text-left transition-[border-color,box-shadow] outline-none select-none [-webkit-touch-callout:none]',
          'hover:border-line-strong hover:shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          task.canEdit && 'touch-manipulation',
          isDragging && 'opacity-40',
        )}
      >
        <CardBody
          task={task}
          today={today}
          reserveDone={task.canEdit && !request}
          reserveAccept={task.canEdit && request}
        />
      </button>
      {task.canEdit && !isDragging && request && (
        <span className="absolute right-3 bottom-3 z-10 flex">
          <Button
            size="s"
            variant="secondary"
            onClick={() => onAccept(task)}
            onPointerDown={(event) => event.stopPropagation()}
            aria-label={`Przyjmij zgłoszenie: ${task.title}`}
            className={TOUCH_BUTTON}
          >
            Przyjmij
          </Button>
        </span>
      )}
      {task.canEdit && !isDragging && !request && (
        // poza przyciskiem karty (bez zagnieżdżonych przycisków); desktop — przy najechaniu lub fokusie
        <span
          className={cn(
            'absolute top-3.5 right-3.5 z-10 flex transition-opacity',
            !alwaysShowDone &&
              '[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/card:opacity-100 [@media(hover:hover)]:focus-within:opacity-100',
          )}
        >
          <DoneToggle done={false} onToggle={() => onComplete(task)} title={task.title} size={18} />
        </span>
      )}
    </li>
  );
}

function CardBody({
  task,
  today,
  reserveDone = false,
  reserveAccept = false,
}: {
  task: TaskSummary;
  today: string;
  reserveDone?: boolean;
  /** miejsce na „Przyjmij” w prawym dolnym rogu (zgłoszenie bez osoby — bez znacznika osoby) */
  reserveAccept?: boolean;
}) {
  const me = useMeId();
  const due = dueInfo(task.dueDate, today, { done: task.status === 'done' });
  const delegated = isDelegatedByMe(task, me);
  const hasChips = Boolean(task.client || task.project || task.service);
  const fromClient = task.origin === 'client_portal';
  // pula godzin usługi zgłoszenia (17.10.2026): „Pula: zostało 1 h 40 min” — decyzja przy przyjęciu zgłoszenia
  const pool = useServicePool(task.status === 'request' ? task.service?.id : null);
  return (
    <>
      <span className={cn('flex items-start gap-1.5', reserveDone && 'pr-6')}>
        <span className="min-w-0 flex-1 text-body-s font-medium break-words text-fg">{task.title}</span>
        <PriorityFlag priority={task.priority} className="mt-0.5" />
      </span>
      {(hasChips || fromClient || pool || task.aiState !== 'none' || task.origin === 'ai') && (
        <span className="mt-2 flex flex-wrap items-center gap-1">
          <FromClientBadge task={task} />
          <TaskChips task={task} wrap className="max-w-full" />
          {pool && <PoolChip usage={pool} size="s" />}
          {!hasChips && task.origin === 'ai' && <span className="text-caption text-fg-muted">od AI</span>}
          <AiStatus state={task.aiState} />
        </span>
      )}
      {/* 10.10.2026: osoba na każdej karcie (awatar jak w menu; bez osoby — neutralny znacznik) */}
      <span
        className={cn('mt-2.5 flex items-center gap-2 text-caption text-fg-muted', reserveAccept && 'min-h-7 pr-20')}
      >
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          {due ? <DueText due={due} /> : <span>Bez terminu</span>}
          {delegated && <DelegatedLabel className="ml-1" />}
        </span>
        {!(reserveAccept && !task.assignee) && <AssigneeAvatar assignee={task.assignee} />}
      </span>
    </>
  );
}

function DoneZone({ dragging, over, count }: { dragging: boolean; over: boolean; count: number }) {
  const { setNodeRef } = useDroppable({ id: DONE_ZONE_ID, disabled: !dragging });
  return (
    <div ref={setNodeRef} className="mt-auto pt-8">
      {dragging ? (
        <div
          className={cn(
            'flex h-14 items-center justify-center gap-2 rounded-[10px] border border-dashed text-body-s transition-colors',
            over ? 'border-success bg-success-subtle text-fg' : 'border-line text-fg-muted',
          )}
        >
          <CircleCheck size={16} strokeWidth={1.75} aria-hidden className={over ? 'text-success' : undefined} />
          Upuść tutaj, aby oznaczyć jako gotowe
        </div>
      ) : (
        <p className="flex items-center gap-1.5 text-body-s text-fg-muted">
          <CircleCheck size={14} strokeWidth={1.75} aria-hidden />
          {count === 0
            ? 'Nic gotowego w tym tygodniu'
            : `${count} ${plural(count, ['gotowe', 'gotowe', 'gotowych'])} w tym tygodniu`}
        </p>
      )}
    </div>
  );
}

function BoardSkeleton({ layout }: { layout: 'grid' | 'snap' }) {
  return (
    <div className="overflow-hidden">
      <div
        role="status"
        aria-label="Wczytywanie tablicy"
        className={cn(layout === 'snap' ? 'flex gap-3' : 'grid min-w-[880px] grid-cols-4 gap-5 lg:gap-7')}
      >
        {BOARD_STATUSES.filter((s) => s !== 'request').map((s, i) => (
          <div key={s} className={cn('flex flex-col gap-2.5', layout === 'snap' && 'w-[84%] max-w-[340px] shrink-0')}>
            <Skeleton className="h-4 w-24" />
            {Array.from({ length: 3 - (i % 2) }, (_, j) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: szkielet
              <Skeleton key={j} className="h-[74px] rounded-[10px]" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
